import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import { 
    Users, 
    Search, 
    Mail, 
    Calendar, 
    Loader2, 
    Trash2, 
    AlertTriangle,
    CheckCircle2,
    XCircle,
    Database,
    FileText,
    Crown
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAdminAxios = () => {
    const token = localStorage.getItem('admin_token');
    return axios.create({
        baseURL: API,
        headers: { Authorization: `Bearer ${token}` }
    });
};

export default function AdminUsers() {
    const navigate = useNavigate();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    
    // Selection state
    const [selectedUsers, setSelectedUsers] = useState(new Set());
    const [selectAll, setSelectAll] = useState(false);
    
    // Delete dialog state
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);
    const [userDataSummary, setUserDataSummary] = useState(null);
    const [loadingSummary, setLoadingSummary] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [deleteData, setDeleteData] = useState(true);
    
    // Bulk delete dialog
    const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
    const [bulkDeleting, setBulkDeleting] = useState(false);
    const [confirmText, setConfirmText] = useState('');

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        try {
            const response = await getAdminAxios().get('/admin/users');
            setUsers(response.data);
        } catch (err) {
            if (err.response?.status === 401) {
                navigate('/admin/login');
            }
            toast.error('Failed to load users');
        } finally {
            setLoading(false);
        }
    };

    const filteredUsers = users.filter(u => 
        u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Handle select all
    const handleSelectAll = (checked) => {
        setSelectAll(checked);
        if (checked) {
            const nonOwnerIds = filteredUsers
                .filter(u => u.role !== 'owner')
                .map(u => u.id);
            setSelectedUsers(new Set(nonOwnerIds));
        } else {
            setSelectedUsers(new Set());
        }
    };

    // Handle individual selection
    const handleSelectUser = (userId, checked) => {
        const newSelected = new Set(selectedUsers);
        if (checked) {
            newSelected.add(userId);
        } else {
            newSelected.delete(userId);
        }
        setSelectedUsers(newSelected);
        setSelectAll(false);
    };

    // Open delete dialog for single user
    const openDeleteDialog = async (user) => {
        setUserToDelete(user);
        setShowDeleteDialog(true);
        setLoadingSummary(true);
        setDeleteData(true);
        
        try {
            const response = await getAdminAxios().get(`/admin/users/${user.id}/data-summary`);
            setUserDataSummary(response.data);
        } catch (err) {
            toast.error('Failed to load user data summary');
            setUserDataSummary(null);
        } finally {
            setLoadingSummary(false);
        }
    };

    // Delete single user
    const handleDeleteUser = async () => {
        if (!userToDelete) return;
        
        setDeleting(true);
        try {
            const response = await getAdminAxios().delete(`/admin/users/${userToDelete.id}?delete_data=${deleteData}`);
            toast.success(response.data.message);
            setShowDeleteDialog(false);
            setUserToDelete(null);
            setUserDataSummary(null);
            fetchUsers();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to delete user');
        } finally {
            setDeleting(false);
        }
    };

    // Bulk delete users
    const handleBulkDelete = async () => {
        if (confirmText !== 'DELETE') {
            toast.error('Please type DELETE to confirm');
            return;
        }
        
        setBulkDeleting(true);
        try {
            const response = await getAdminAxios().post('/admin/users/bulk-delete', {
                user_ids: Array.from(selectedUsers),
                delete_data: deleteData
            });
            
            toast.success(`Deleted ${response.data.deleted_count} users`);
            setShowBulkDeleteDialog(false);
            setSelectedUsers(new Set());
            setSelectAll(false);
            setConfirmText('');
            fetchUsers();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to delete users');
        } finally {
            setBulkDeleting(false);
        }
    };

    return (
        <div>
            <div className="mb-6 flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-white">Users</h1>
                    <p className="text-slate-400">Manage MediGuide users ({users.length} total)</p>
                </div>
                
                {selectedUsers.size > 0 && (
                    <Button 
                        variant="destructive"
                        onClick={() => setShowBulkDeleteDialog(true)}
                        className="bg-red-600 hover:bg-red-700"
                    >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete Selected ({selectedUsers.size})
                    </Button>
                )}
            </div>

            {/* Search and Select All */}
            <Card className="bg-slate-800 border-slate-700 mb-6">
                <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <Input 
                                placeholder="Search by name or email..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-10 bg-slate-700 border-slate-600 text-white"
                            />
                        </div>
                        <div className="flex items-center gap-2">
                            <Checkbox 
                                id="select-all"
                                checked={selectAll}
                                onCheckedChange={handleSelectAll}
                            />
                            <label htmlFor="select-all" className="text-sm text-slate-400 cursor-pointer">
                                Select All
                            </label>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Users List */}
            {loading ? (
                <div className="flex justify-center py-12">
                    <Loader2 className="w-8 h-8 text-indigo-500 animate-spin" />
                </div>
            ) : filteredUsers.length === 0 ? (
                <Card className="bg-slate-800 border-slate-700">
                    <CardContent className="py-12 text-center">
                        <Users className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                        <p className="text-slate-400">No users found</p>
                    </CardContent>
                </Card>
            ) : (
                <div className="space-y-3">
                    {filteredUsers.map((user) => (
                        <Card 
                            key={user.id} 
                            className={`bg-slate-800 border-slate-700 transition-all ${
                                selectedUsers.has(user.id) ? 'ring-2 ring-indigo-500' : ''
                            }`}
                        >
                            <CardContent className="p-4">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-4">
                                        {user.role !== 'owner' ? (
                                            <Checkbox 
                                                checked={selectedUsers.has(user.id)}
                                                onCheckedChange={(checked) => handleSelectUser(user.id, checked)}
                                            />
                                        ) : (
                                            <div className="w-4" /> 
                                        )}
                                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-white font-bold">
                                            {user.role === 'owner' ? (
                                                <Crown className="w-5 h-5" />
                                            ) : (
                                                user.name?.charAt(0) || 'U'
                                            )}
                                        </div>
                                        <div>
                                            <h3 className="font-medium text-white flex items-center gap-2">
                                                {user.name}
                                                {user.role === 'owner' && (
                                                    <span className="px-2 py-0.5 rounded-full text-xs bg-amber-500/20 text-amber-400">
                                                        Owner
                                                    </span>
                                                )}
                                            </h3>
                                            <div className="flex items-center gap-4 text-xs text-slate-400">
                                                <span className="flex items-center gap-1">
                                                    <Mail className="w-3 h-3" />
                                                    {user.email}
                                                </span>
                                                <span className="flex items-center gap-1">
                                                    <Calendar className="w-3 h-3" />
                                                    {new Date(user.created_at).toLocaleDateString()}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-4">
                                        <div className="text-right">
                                            <p className="text-sm text-slate-400">
                                                {user.usage_count || 0} checks
                                            </p>
                                            {user.subscription?.plan && (
                                                <span className="px-2 py-1 rounded-full text-xs bg-indigo-500/20 text-indigo-400">
                                                    {user.subscription.plan}
                                                </span>
                                            )}
                                        </div>
                                        {user.role !== 'owner' && (
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => openDeleteDialog(user)}
                                                className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}

            {/* Single User Delete Dialog */}
            <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
                <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-red-400">
                            <AlertTriangle className="w-5 h-5" />
                            Delete User
                        </DialogTitle>
                        <DialogDescription className="text-slate-400">
                            This action cannot be undone.
                        </DialogDescription>
                    </DialogHeader>
                    
                    {loadingSummary ? (
                        <div className="flex justify-center py-8">
                            <Loader2 className="w-6 h-6 text-indigo-500 animate-spin" />
                        </div>
                    ) : userDataSummary && (
                        <div className="space-y-4">
                            <div className="p-3 bg-slate-800 rounded-lg">
                                <p className="font-medium">{userDataSummary.user?.name}</p>
                                <p className="text-sm text-slate-400">{userDataSummary.user?.email}</p>
                            </div>
                            
                            <div className="space-y-2">
                                <p className="text-sm font-medium flex items-center gap-2">
                                    <Database className="w-4 h-4" />
                                    Associated Data ({userDataSummary.total_records} records)
                                </p>
                                <div className="grid grid-cols-2 gap-2 text-xs">
                                    {Object.entries(userDataSummary.data_counts || {}).map(([key, count]) => (
                                        <div key={key} className="flex justify-between p-2 bg-slate-800 rounded">
                                            <span className="text-slate-400">{key.replace(/_/g, ' ')}</span>
                                            <span className="text-white">{count}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            
                            <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                                <Checkbox 
                                    id="delete-data"
                                    checked={deleteData}
                                    onCheckedChange={setDeleteData}
                                />
                                <label htmlFor="delete-data" className="text-sm cursor-pointer">
                                    Also delete all associated data
                                </label>
                            </div>
                        </div>
                    )}
                    
                    <DialogFooter className="gap-2">
                        <Button 
                            variant="outline" 
                            onClick={() => setShowDeleteDialog(false)}
                            className="border-slate-600 text-slate-300"
                        >
                            Cancel
                        </Button>
                        <Button 
                            variant="destructive"
                            onClick={handleDeleteUser}
                            disabled={deleting}
                            className="bg-red-600 hover:bg-red-700"
                        >
                            {deleting ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Deleting...
                                </>
                            ) : (
                                <>
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    Delete User
                                </>
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Bulk Delete Dialog */}
            <Dialog open={showBulkDeleteDialog} onOpenChange={setShowBulkDeleteDialog}>
                <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-red-400">
                            <AlertTriangle className="w-5 h-5" />
                            Bulk Delete Users
                        </DialogTitle>
                        <DialogDescription className="text-slate-400">
                            You are about to delete {selectedUsers.size} user(s). This action cannot be undone.
                        </DialogDescription>
                    </DialogHeader>
                    
                    <div className="space-y-4">
                        <div className="max-h-40 overflow-y-auto space-y-1 p-3 bg-slate-800 rounded-lg">
                            {Array.from(selectedUsers).map(userId => {
                                const user = users.find(u => u.id === userId);
                                return user ? (
                                    <div key={userId} className="flex items-center gap-2 text-sm">
                                        <XCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
                                        <span className="text-slate-300">{user.email}</span>
                                    </div>
                                ) : null;
                            })}
                        </div>
                        
                        <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                            <Checkbox 
                                id="bulk-delete-data"
                                checked={deleteData}
                                onCheckedChange={setDeleteData}
                            />
                            <label htmlFor="bulk-delete-data" className="text-sm cursor-pointer">
                                Also delete all associated data
                            </label>
                        </div>
                        
                        <div>
                            <p className="text-sm text-slate-400 mb-2">
                                Type <span className="font-bold text-red-400">DELETE</span> to confirm:
                            </p>
                            <Input 
                                value={confirmText}
                                onChange={(e) => setConfirmText(e.target.value)}
                                placeholder="DELETE"
                                className="bg-slate-800 border-slate-600 text-white"
                            />
                        </div>
                    </div>
                    
                    <DialogFooter className="gap-2">
                        <Button 
                            variant="outline" 
                            onClick={() => {
                                setShowBulkDeleteDialog(false);
                                setConfirmText('');
                            }}
                            className="border-slate-600 text-slate-300"
                        >
                            Cancel
                        </Button>
                        <Button 
                            variant="destructive"
                            onClick={handleBulkDelete}
                            disabled={bulkDeleting || confirmText !== 'DELETE'}
                            className="bg-red-600 hover:bg-red-700"
                        >
                            {bulkDeleting ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Deleting...
                                </>
                            ) : (
                                <>
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    Delete {selectedUsers.size} Users
                                </>
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
